import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { cn } from "../../utils/constants/cn";
import { useAnalytics } from "../../hook/useAnalytics";
import { MonthlyData, PropertyData, Reports } from "../../hook/types/analytics";
import {
  FaHome,
  FaMoneyBillWave,
  FaPhone,
  FaSpinner,
  FaUser,
} from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { User } from "../../hook/types/users";
import { useUsers } from "../../hook/useUsers";
import { FiAlertCircle } from "react-icons/fi";

import OutstandingAmountSlider, { PendingPayment, UpcomingPayment, CurrentPayment } from "./OutstandingAmountSlider";
import { useProperties } from "../../hook/useProperties";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);
// Loader Component
const Loader = () => (
  //use h-96 x h-64
  <div className="flex justify-center items-center h-[394px]"> 
    <FaSpinner className="animate-spin text-4xl text-primary" />
  </div>
);
type monthlyData = {
  [key: string]: number;
};

interface PropertyItem {
  occupied: number;
  unoccupied: number;
  occupiedProperties: string[];
  unoccupiedProperties: string[];
}

interface PropertiesDataType {
  data: PropertyItem[];
}
interface PropertyDataPie {
  occupied: number;
  unoccupied: number;
  occupiedProperties: string[];
  unoccupiedProperties: string[];
}

interface Property {
  id: number;
  propertyName: string;
  propertyAddress: string;
  propertyType: string;
  propertyRent: number;
  propertyStatus: string;
  noOfRooms: number;
  propertyDepositAmount: number;
  propertySize: number;
  isActive: boolean;
  propertyImageUrl: string | null;
  tenantName: string | null;
  tenantContact: string | null;
}

interface ApiResponse {
  status: number;
  message: string;
  data: Property[];
}
interface RentStatusProps {
  paid: number;
  unpaid: number;
  balance: number;
  monthlyIncome: number;
}
interface MonthlyIncomeProps {
  totalDeposit: number;
  previousMonth: number;
}
interface BalanceAmount {
  totalAmount1: number;
  monthData1: number[];
  totalAmount2: number;
  monthData2: Tenant[];
 totalExpenses: number; // Add this
  expensesData: number[]; // Add this
  detailedPendingPayments?: {
    current: CurrentPayment[];
    pending: PendingPayment[];
    upcoming: UpcomingPayment[];
  };
   selectedYear: number;
  selectedProperty: string;
  onYearChange: (year: number) => void;
  onPropertyChange: (propertyId: string) => void;
   currentIndex: number; // Add this
  onIndexChange: (index: number) => void; // Add this
}

interface Tenant {
  tenantName: string;
  contact: string;
  //pendingAmount: number;
  openingBalance : number;
  rent : number;
  depositAmount : number;
  dueDate: string;
  isCurrent: boolean;
}

interface TenantListProps {
  tenants: Tenant[];
}


const fetchAllProperties = async () => {
  try {
    const token = localStorage.getItem("R_M_t");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(
      `https://api.selvamrentalmanagement.space/api/v1/getPropertyPage?PageNo=0&count=1000`, // Endpoint to fetch all properties
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data.data.content || []; // Return the `content` array
  } catch (error) {
    console.error("Error fetching all properties:", error);
    return [];
  }
};

const formatPropertiesData = (allProperties: Property[]) => {
  const residentialOccupied = allProperties.filter(
    (p) => p.propertyType === "RESIDENTIAL" && p.propertyStatus === "OCCUPIED"
  );
  const residentialUnoccupied = allProperties.filter(
    (p) => p.propertyType === "RESIDENTIAL" && p.propertyStatus === "UNOCCUPIED"
  );
  const commercialOccupied = allProperties.filter(
    (p) => p.propertyType === "COMMERCIAL" && p.propertyStatus === "OCCUPIED"
  );
  const commercialUnoccupied = allProperties.filter(
    (p) => p.propertyType === "COMMERCIAL" && p.propertyStatus === "UNOCCUPIED"
  );

  return [
    {
      occupied: residentialOccupied.length,
      unoccupied: residentialUnoccupied.length,
      occupiedProperties: residentialOccupied.map((p) => p.propertyName),
      unoccupiedProperties: residentialUnoccupied.map((p) => p.propertyName),
    },
    {
      occupied: commercialOccupied.length,
      unoccupied: commercialUnoccupied.length,
      occupiedProperties: commercialOccupied.map((p) => p.propertyName),
      unoccupiedProperties: commercialUnoccupied.map((p) => p.propertyName),
    },
  ];
};



const Properties: React.FC<PropertiesDataType> = ({ data }) => {
 //console.log("Properties Data inside:", data); 
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHoveredOccupied, setIsHoveredOccupied] = useState(false);
  const [isHoveredUnoccupied, setIsHoveredUnoccupied] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
 
  
  useEffect(() => {
    if (data.length > 0) {
      setIsLoading(false); // Data is available, stop loading
    }
  }, [data]);

  useEffect(() => {
    if (!isHoveredOccupied && !isHoveredUnoccupied) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === data.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [data.length, isHoveredOccupied, isHoveredUnoccupied]);
  
  if (isLoading) {
    return <Loader
   // className="w-full h-full flex items-center justify-center"
    />; // Show loader while data is being fetched
  }
  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {data.map((item, index) => (
          <div
            key={index}
            className="min-w-full h-full flex items-center justify-center rounded-lg"
          >
            <div className="p-3 max-w-xl mx-auto">
              <h3 className="text-lg font-semibold mb-4 text-center">
                {index === 0 ? "Residential" : "Commercial"} Properties
              </h3>
              <div className="flex justify-center items-center">
                <div className="flex xl:p-2 xl:w-64 justify-center h-64">
                  <Pie
                    data={{
                      labels: ["Occupied", "Unoccupied"],
                      datasets: [
                        {
                          data: [item.occupied, item.unoccupied],
                          backgroundColor: ["#58d68d", "#e43c3c"],
                          hoverBackgroundColor: ["#28b463", "#EF4444"],
                        },
                      ],
                    }}
                    options={{ maintainAspectRatio: false }}
                  />
                </div>
              </div>

              {/* Occupied */}
              <div className="flex justify-center mt-4 gap-4">
                <div
                  className="relative group"
                  onMouseEnter={() => setIsHoveredOccupied(true)}
                  onMouseLeave={() => setIsHoveredOccupied(false)}
                >
                  <span className="bg-white shadow-md p-1 px-2 rounded-lg border hover:bg-green-100 cursor-pointer flex items-center gap-2">
                    <FaHome className="text-green-500" /> Occupied: {item.occupied}
                  </span>
                  {isHoveredOccupied && item.occupiedProperties && (
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-max bg-gray-800 text-white text-sm rounded-md px-2 py-1">
                      <ul>
                        {item.occupiedProperties.map((property, i) => (
                          <li key={i}>{property}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Unoccupied */}
                <div
                  className="relative group"
                  onMouseEnter={() => setIsHoveredUnoccupied(true)}
                  onMouseLeave={() => setIsHoveredUnoccupied(false)}
                >
                  <span className="bg-white shadow-md p-1 px-2 rounded-lg border hover:bg-red-100 cursor-pointer flex items-center gap-2">
                    <FaHome className="text-red-500" /> Unoccupied: {item.unoccupied}
                  </span>
                  {isHoveredUnoccupied && item.unoccupiedProperties && (
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-max bg-gray-800 text-white text-sm rounded-md px-2 py-1">
                      <ul>
                        {item.unoccupiedProperties.map((property, i) => (
                          <li key={i}>{property}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Indicator */}
              <div className="flex justify-center mt-4">
                {data.map((_, i) => (
                  <span
                    key={i}
                    onClick={() => handleIndicatorClick(i)}
                    className={`h-2 w-2 mx-1 rounded-full cursor-pointer ${
                      i === currentIndex ? "bg-blue-500" : "bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};




const RentStatus: React.FC<RentStatusProps> = ({
  balance,
  paid,
  monthlyIncome,
  unpaid,
}) => (
  <div className="border p-4 rounded-lg flex flex-col gap-2 shadow-md bg-white">
    <h3 className="text-lg font-semibold ">Rent (Current Month)</h3>
    <div className="text-md">
      Total: <span className="font-medium ">{paid+unpaid}</span>
    </div>
    <div className="text-md">
      Paid: <span className="font-medium ">{paid}</span>
    </div>
    <div className="text-md">
      Unpaid: <span className="font-medium ">{unpaid}</span>
    </div>
  
  </div>
);

const MonthlyIncome: React.FC<MonthlyIncomeProps> = ({
  previousMonth,
  totalDeposit,
}) => (
  <div className="border p-4 rounded-lg shadow-md bg-white flex flex-col justify-center gap-2">
    <h3 className="text-lg font-semibold ">Summary</h3>

    <div className="font- text-md">
      Total Deposit Amount:{" "}
      <span className="font-medium capitalize">{totalDeposit}</span>
    </div>
    <div className="font- text-md">
      Total Rent Balance:{" "}
      <span className="font-medium capitalize">{previousMonth}</span>
    </div>
  </div>
);

const BalanceAmount: React.FC<BalanceAmount> = ({
  monthData1,
  monthData2,
  totalAmount1,
  detailedPendingPayments,
  totalAmount2,
  totalExpenses,
  expensesData,
   selectedYear,
  selectedProperty,
  onYearChange,
  onPropertyChange,
 currentIndex, // Receive from props
  onIndexChange,
 // pendingCommercial, // Receive the commercial pending payments
  //pendingResidential, // Receive the residential pending payments
}) => {
  // const [currentIndex, setCurrentIndex] = useState(0);
   const [isInteracting, setIsInteracting] = useState(false);
  const [interactionTimeout, setInteractionTimeout] = useState<NodeJS.Timeout | null>(null);
  
    const { Property, queryClient } = useProperties();
    const PropertyData = Property({ active: true, delete: false });
      const allProperties = [
    ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
    ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
  ];


  //  const yearOptions = Array.from({ length: 6 }, (_, i) => {
  //     const year = new Date().getFullYear() - i;
  //     return { value: year, label: year.toString() };
  //   });
 const currentYear = new Date().getFullYear();
const startYear = 2010;
const yearOptions = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
  const year = currentYear - i;
  return { value: year, label: year.toString() };
});
    // Generate property dropdown options
    const propertyOptions = [
      { value: "", label: "All Properties" },
      ...allProperties.map(prop => ({
        value: prop.id?.toString(),
        label: prop.propertyName
      }))
    ];

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onYearChange(parseInt(e.target.value));
  };

  const handlePropertyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onPropertyChange(e.target.value);
  };

  const slides = [
    {
      type: "pendingCombined",
      component: (
        <OutstandingAmountSlider
          current={detailedPendingPayments?.current ?? []}
          pending={detailedPendingPayments?.pending ?? []}
          upcoming={detailedPendingPayments?.upcoming ?? []}
        />
      ),
    },
    { type: "tenantList", data: monthData2 },
    { type: "chart", data: { monthData: monthData1, total: totalAmount1 } },
    { type: "expensesChart", data: { monthData: expensesData, total: totalExpenses } },
  ];
  
    // Handle auto sliding with freeze functionality
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    // Only start auto-sliding if not interacting
    if (!isInteracting) {
      intervalId = setInterval(() => {
      //   setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
      // }, 60000); // Slide every 60 seconds
      onIndexChange((currentIndex + 1) % slides.length); // Use onIndexChange
      }, 60000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [slides.length, isInteracting]);

  const handleIndicatorClick = (index: number) => {
   // setCurrentIndex(index);
     onIndexChange(index);
    // Temporarily freeze when user manually changes slide
    setIsInteracting(true);
    
    // Clear existing timeout
    if (interactionTimeout) {
      clearTimeout(interactionTimeout);
    }
    
    // Resume auto-sliding after 10 seconds of inactivity
    const timeout = setTimeout(() => {
      setIsInteracting(false);
    }, 10000);
    
    setInteractionTimeout(timeout);
  };

  const handleSlideInteraction = (isEntering: boolean) => {
    if (isEntering) {
      // User is interacting with the slide
      setIsInteracting(true);
      
      // Clear existing timeout
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
      }
    } else {
      // User stopped interacting with the slide
      // Resume auto-sliding after 5 seconds of inactivity
      const timeout = setTimeout(() => {
        setIsInteracting(false);
      }, 5000);
      
      setInteractionTimeout(timeout);
    }
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (interactionTimeout) {
        clearTimeout(interactionTimeout);
      }
    };
  }, [interactionTimeout]);

  const createChartData = (monthData: number[], label: string, isExpense = false) => ({
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Total Rent Amount Collected",
        data: monthData,
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return;
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          // gradient.addColorStop(0, "#7955ed");
          // gradient.addColorStop(0.5, "#d754e8");
          // gradient.addColorStop(1, "#e854aa");
           if (isExpense) {
          gradient.addColorStop(0, "#ff7e5f");
          gradient.addColorStop(0.5, "#feb47b");
          gradient.addColorStop(1, "#ff6a6a");
        } else {
          gradient.addColorStop(0, "#7955ed");
          gradient.addColorStop(0.5, "#d754e8");
          gradient.addColorStop(1, "#e854aa");
        }
          return gradient;
        },
      },
    ],
  });

  const options = {
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { display: false } },
    },
  };

  // Handle auto sliding for 3 
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
  //   }, 60000); // Slide every 60 seconds
  //   return () => clearInterval(interval);
  // }, [slides.length]);

  // const handleIndicatorClick = (index: number) => {
  //   setCurrentIndex(index);
  // };
//  console.log("rent reposne ",monthData1)
//  console.log("expenses reposne ", expensesData)

 
  return (
    <div className="relative">

    {(currentIndex === 2 || currentIndex === 3) && (
        <div className="flex flex-wrap gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-col">
            {/* <label className="text-sm font-medium mb-1">Year</label> */}
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {yearOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex flex-col">
            {/* <label className="text-sm font-medium mb-1">Property</label> */}
            <select
              value={selectedProperty}
              onChange={handlePropertyChange}
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[150px]"
            >
              {propertyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    
    {currentIndex == 0 ? (
  <h3 className="text-lg font-semibold mb-2">Rent Summary Breakdown</h3>
) : currentIndex == 1 ? (
  <h3 className="text-lg font-semibold mb-4">Outstanding Amount</h3>
) : (
  <h3 className="text-lg font-semibold mb-4">
    Total Rent Collected (Current Year)
  </h3>
)}


        <div className="overflow-hidden w-full">
        <div className="flex">
          <AnimatePresence mode="wait">
            {currentIndex === 0 && (
              <motion.div
                key="summary"
                className="w-full flex-shrink-0 text-center py-10"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => handleSlideInteraction(true)}
                onMouseLeave={() => handleSlideInteraction(false)}
                onTouchStart={() => handleSlideInteraction(true)}
                onTouchEnd={() => handleSlideInteraction(false)}
              >
                <OutstandingAmountSlider
                  current={detailedPendingPayments?.current ?? []}
                  pending={detailedPendingPayments?.pending ?? []}
                  upcoming={detailedPendingPayments?.upcoming ?? []}
                />
              </motion.div>
            )}

            {currentIndex === 1 && (
              <motion.div
                key="tenantList"
                className="w-full flex-shrink-0"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => handleSlideInteraction(true)}
                onMouseLeave={() => handleSlideInteraction(false)}
                onTouchStart={() => handleSlideInteraction(true)}
                onTouchEnd={() => handleSlideInteraction(false)}
              >
                <TenantList tenants={monthData2} />
              </motion.div>
            )}

            {currentIndex === 2 && (
              <motion.div
                key="chart"
                className="w-full flex-shrink-0"
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => handleSlideInteraction(true)}
                onMouseLeave={() => handleSlideInteraction(false)}
                onTouchStart={() => handleSlideInteraction(true)}
                onTouchEnd={() => handleSlideInteraction(false)}
              >
                <p className="text-center">Total Rent: ₹{totalAmount1?.toFixed(2) ?? '0.00'}</p>
                <div className="w-full md:h-44 lg:h-84 xl:h-96 flex justify-center items-center">
                  {/* <Bar data={createChartData(monthData1)} options={options} /> */}
                    <Bar data={createChartData(monthData1, "Total Rent Collected")} options={options} />
                </div>
              </motion.div>
            )}
            {currentIndex === 3 && (
  <motion.div
    key="expensesChart"
    className="w-full flex-shrink-0"
    initial={{ opacity: 0, x: -100 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 100 }}
    transition={{ duration: 0.3 }}
  >
    <p className="text-center">Total Expenses: ₹{totalExpenses?.toFixed(2) ?? '0.00'}</p>
    <div className="w-full md:h-44 lg:h-84 xl:h-96 flex justify-center items-center">
      <Bar data={createChartData(expensesData, "Total Expenses", true)} options={options} />
    </div>
  </motion.div>
)}
          </AnimatePresence>
        </div>
      </div>

      {/* Centered Indicators with freeze status */}
      <div className="flex justify-center items-center mt-4 space-x-2">
        <div className="flex">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              className={`w-2 h-2 mx-1 rounded-full transition-colors duration-200 ${
                currentIndex === index ? "bg-primary" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
        
        {/* Freeze indicator */}
     {isInteracting && (
          <div className="ml-4 flex items-center">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-1" />
            <span className="text-xs text-gray-500">Paused</span>
          </div>
        )}
      </div>
    </div>
  );
};

const TenantList: React.FC<TenantListProps> = ({ tenants }) => {
  const profileColors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-red-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
  ];
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the bottom of the list
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [tenants]);

  const currentTenants = tenants.filter((tenant) => tenant.isCurrent);
  const pastTenants = tenants.filter((tenant) => !tenant.isCurrent);

  return (
    <div
      className="p-4 w-full mx-auto h-96 overflow-y-auto border border-gray-300 rounded-lg shadow-lg bg-white"
      ref={containerRef}
    >

      <ul className="list-none p-0 mb-6">
        {currentTenants.length == 0 ? (
          <p>
       
          </p>
        ) : (
          currentTenants.map((tenant, index) => (
            <motion.li
              key={index}
              className="flex items-center mb-4 p-4 border cursor-pointer border-gray-200 rounded-lg transition-all duration-300 hover:bg-primary/10 hover:shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              whileHover={{ scale: 1.03 }}
            >
              <div
                className={`w-16 h-16 rounded-full text-white flex items-center justify-center mr-3 font-bold text-lg shadow-md ${
                  profileColors[index % profileColors.length]
                }`}
              >
                {tenant.tenantName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
              
                  <strong className="block text-xs sm:text-base"> 
                  {tenant.tenantName}
                </strong>
                <div className="flex items-center mt-1 text-black text-xs sm:text-base">
                  <FaPhone className="mr-1 text-blue-500" />
                  <span>{tenant.contact}</span>
                </div>
                <div className="flex items-center mt-1 text-black text-xs sm:text-base">
                  <FaMoneyBillWave className="mr-1 text-green-500" />
                  {/* <span>
                    Pending Amount: ₹{tenant.pendingAmount.toFixed(2)}
                  </span> */}
                  
                  Opening Balance : ₹{tenant.openingBalance.toFixed(2)}
                </div>
               
                <div className="flex items-center mt-1 text-gray-600 text-xs sm:text-base">
                  <FaMoneyBillWave className="mr-1 text-green-500" />
               
                  <span>
                  Deposit Amount : ₹{tenant.depositAmount.toFixed(2)}
                  </span>
                  
                </div>

            
              </div>
            </motion.li>
          ))
        )}
      </ul>

    
      <ul className="list-none p-0">
        {pastTenants.length == 0 ? (
          <p>
        
          </p>
        ) : (
          pastTenants.map((tenant, index) => (
            <motion.li
            key={index}
            className="flex flex-col md:flex-row items-center mb-4 p-4 border cursor-pointer border-gray-200 rounded-lg transition-all duration-300 hover:bg-primary/10 hover:shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            whileHover={{ scale: 1.03 }}
          >
            {/* Profile Avatar */}
            <div
              className={`w-16 h-16 rounded-full text-white flex items-center justify-center mr-3 font-bold text-lg shadow-md ${
                profileColors[index % profileColors.length]
              }`}
            >
              {tenant.tenantName.charAt(0).toUpperCase()}
            </div>
            
            {/* Tenant Details */}
            <div className="flex-1 mt-3 md:mt-0">
              <strong className="block text-lg text-gray-800">{tenant.tenantName}</strong>
              
              <div className="flex items-center mt-2 text-gray-600 text-xs sm:text-base">
                <FaPhone className="mr-1 text-blue-500" />
                Tenant Phone Number : <span>{tenant.contact}</span>
              </div>

<div className="flex items-center mt-1 text-gray-600 text-xs sm:text-base">
                  <FaMoneyBillWave className="mr-1 text-green-500" />
                  
                  Opening Balance : ₹{tenant.openingBalance.toFixed(2)}
                </div>
               
                <div className="flex items-center mt-1 text-gray-600 text-xs sm:text-base">
                  <FaMoneyBillWave className="mr-1 text-green-500" />
                
                  <span>
                  Deposit Amount : ₹{tenant.depositAmount.toFixed(2)}
                  </span>
                  
                </div>
     
            </div>
          </motion.li>
          
          ))
        )}
      </ul>
    </div>
  );
};


function Dashboard() {
  const [propertiesData, setPropertiesData] = useState<PropertyItem[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedProperty, setSelectedProperty] = useState("");
const [balanceSliderIndex, setBalanceSliderIndex] = useState(0);
   const { Property } = useProperties();
  const PropertyData = Property({ active: true, delete: false });
  const allProperties = [
    ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
    ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
  ];

  useEffect(() => {
    const fetchData = async () => {
      const allProperties = await fetchAllProperties();
      const formattedData = formatPropertiesData(allProperties);
      setPropertiesData(formattedData);
      setIsLoading(false); // Data is fetched, stop loading
    };

    fetchData();
  }, []);

   const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year, label: year.toString() };
  });

  // Generate property options
  const propertyOptions = [
    { value: "", label: "All Properties" },
    ...allProperties.map(prop => ({
      value: prop.id?.toString(),
      label: prop.propertyName
    }))
  ];

  // const convertToNumberArray = (data: monthlyData): number[] => {
  //   return Object.values(data);
  // };
    // Fixed function to convert monthly data to array in correct order
  const convertToNumberArray = (data: monthlyData): number[] => {
    const monthOrder = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    
    return monthOrder.map(month => data[month] || 0);
  };

  const {
    currentMonthData,
    queryClient,
    currentPendingAmount,
    currentYearRentCollection,
     currentYearExpenses, 
    dashboardCounts,
    report,
    detailedPendingPayments,
  // } = useAnalytics();
    } = useAnalytics(selectedYear, selectedProperty);

 const CurrentYearRentCollection = currentYearRentCollection?.data;
  const CurrentYearExpenses = currentYearExpenses?.data;
  
  // Debug logs
  // console.log("CurrentYearRentCollection:", CurrentYearRentCollection);
  // console.log("CurrentYearExpenses:", CurrentYearExpenses);
  // console.log("Selected Year:", selectedYear);
  // console.log("Selected Property:", selectedProperty);
  const uid = localStorage.getItem("R_M_uid");

  const userData = queryClient.getQueryData(["users", uid]) as User;

  const propertiesdata = queryClient.getQueryData([
    "dashboardC_counts",
  ]) as PropertyData;
  const CurrentMonthData = queryClient.getQueryData(["current-month-data"]) as {
    data: { total : number; paid: number; unpaid: number };
  };
  // const CurrentYearRentCollection = queryClient.getQueryData([
  //   "current_year_rent_collection",
  // ]) as MonthlyData;
  const Report = queryClient.getQueryData(["report"]) as Reports;
//   console.log("Report Data", detailedPendingPayments);
//   console.log(detailedPendingPayments.data.pending);
// Add these debug logs in your Dashboard component


// const CurrentYearExpenses = queryClient.getQueryData([
//   "current_year_expenses",
// ]) as MonthlyData;

  const CurrentPendingAmount = queryClient.getQueryData([
    "current-month-pending-rent",
  ]) as Tenant[];


  if (
    currentPendingAmount.isLoading ||
    currentMonthData.isLoading ||
    currentYearRentCollection.isLoading ||
    dashboardCounts.isLoading ||
    report.isLoading ||
    detailedPendingPayments.isLoading 
  ) {
    return (
      <div className="flex h-screen justify-center items-center text-center ">
        <FaSpinner className="animate-spin mr-2" /> Loading...{" "}
      </div>
    );
  }

  
  return (
    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 p-4 gap-4 w-full">
      {/* Properties */}

      <div className="flex flex-col gap-4">
        <div className="w-full border flex-col rounded-lg shadow-md flex justify-center items-center bg-white">
       


{isLoading ? <Loader /> : <Properties data={propertiesData} />}

{/* <Properties data={propertiesData} /> */}
        </div>
        {userData?.roles[0]?.roleName === "ADMIN" ? (
          <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
            <RentStatus
              paid={CurrentMonthData.data.paid}
              unpaid={CurrentMonthData.data.unpaid}
              monthlyIncome={80000}
              balance={7000}
            />
          
          <MonthlyIncome
  totalDeposit={Report?.data?.totalDepositAmount ?? 0}
  previousMonth={Report?.data?.totalPendingRent ?? 0}
/>

          </div>
        ) : null}
      </div>

      {/* Main content */}
      <div className="lg:col-span-2 md:col-span-1 grid grid-cols-1 gap-4">

      
       
        {/* Balance Amount */}
        <div className="col-span-1 h-full border  rounded-lg shadow-md bg-white p-2 md:p-4  md:flex md:justify-center md:items-center">
          <div className="md:w-full md:px-4">
           
             {/* <BalanceAmount
                            totalAmount1={CurrentYearRentCollection?.data?.totalAmount ?? 0}
                         monthData1={convertToNumberArray(
  CurrentYearRentCollection?.data?.monthlySummary ?? {}
)}
                            totalAmount2={CurrentYearRentCollection?.data?.totalAmount ?? 0}
                            monthData2={CurrentPendingAmount ?? []}
                          totalExpenses={CurrentYearExpenses?.data?.totalAmount ?? 0} // Add this
  expensesData={convertToNumberArray(
    CurrentYearExpenses?.data?.monthlySummary ?? {}
  )}
                            detailedPendingPayments={detailedPendingPayments?.data}
                             selectedYear={selectedYear}
              selectedProperty={selectedProperty}
              onYearChange={setSelectedYear}
              onPropertyChange={setSelectedProperty}

                        /> */}
                       
<BalanceAmount
  totalAmount1={CurrentYearRentCollection?.data?.totalAmount ?? 0}
  monthData1={convertToNumberArray(
    CurrentYearRentCollection?.data?.monthlySummary ?? {}
  )}
  totalAmount2={CurrentYearRentCollection?.data?.totalAmount ?? 0}
  monthData2={CurrentPendingAmount ?? []}
  totalExpenses={CurrentYearExpenses?.data?.totalAmount ?? 0}
  expensesData={convertToNumberArray(
    CurrentYearExpenses?.data?.monthlySummary ?? {}
  )}
  detailedPendingPayments={detailedPendingPayments?.data}
  selectedYear={selectedYear}
  selectedProperty={selectedProperty}
  onYearChange={setSelectedYear}
  onPropertyChange={setSelectedProperty}
  currentIndex={balanceSliderIndex} // Add this
  onIndexChange={setBalanceSliderIndex} // Add this
/>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;